import { formatElapsed, type CaptureLossReason } from '@/stores/recording'
import { detectCaptureClient } from '@/lib/capture-heartbeat'

export interface CaptureLostCopy {
  title: string
  detail: string
  /** The finish button's label. */
  finish: string
}

/**
 * Copy for the mic-lost notice (CLA-1341); split out so the component file stays refresh-safe.
 *
 * `onBackup`: the main recorder stopped but the segment roller is still
 * recording the consult (CLA-1552). Nothing has been lost, so the notice
 * says so instead of counting what was captured — the frozen number would
 * understate what finishing will keep.
 */
export function captureLostCopy(
  reason: CaptureLossReason,
  elapsed: number,
  onBackup = false,
): CaptureLostCopy {
  if (onBackup) {
    return {
      title: 'Main recorder stopped — still recording on backup',
      detail: 'Finish when you’re done.',
      finish: 'Finish recording',
    }
  }
  return { ...lostCopy(reason, elapsed), finish: 'Finish with what was captured' }
}

function lostCopy(reason: CaptureLossReason, elapsed: number) {
  const captured = `${formatElapsed(elapsed)} captured`
  const ios = detectCaptureClient() === 'web_ios'
  switch (reason) {
    case 'muted':
      return {
        title: ios ? 'Your iPhone paused the microphone' : 'The microphone stopped sending audio',
        detail: ios
          ? `Safari stops recording when the screen locks or you switch apps. ${captured}. Keep the screen on to continue, or finish now.`
          : `${captured}. If it comes back, recording continues automatically — or finish now.`,
      }
    case 'ended':
      return {
        title: 'The microphone was disconnected',
        detail: `${captured}. Finish to keep what was recorded, then record again if needed.`,
      }
    case 'error':
      return {
        title: 'Recording stopped unexpectedly',
        detail: `${captured}. Finish to keep what was recorded, then record again if needed.`,
      }
  }
}

